import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatLayers } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import PrintSessionBanner from "@/components/PrintSessionBanner";
import { usePrintPage } from "@/lib/usePrintPage";
import { ArrowLeft, Printer } from "lucide-react";

const LABEL_TYPE_LABELS = {
    own: "Ons eigen etiket",
    customer: "Klant eigen etiket",
    none: "Geen etiket",
};

/**
 * Bulk-print pagina voor order-samenvattingen. Plakt N samenvattingen onder
 * elkaar (1 per order, gefilterd op locatie) — elke samenvatting is een
 * `.print-page` met automatische page-break tussen orders.
 *
 * Query-string:
 *   ?ids=A,B,C
 *   &location_id=<optional>
 *   &auto_print=1
 *   &print_session=<id>
 */
export default function PrintSummariesCombined() {
    usePrintPage("a4");
    const [searchParams] = useSearchParams();
    const idsStr = searchParams.get("ids") || "";
    const locationIdsParam = searchParams.get("location_ids") || "";
    const legacyLocationId = searchParams.get("location_id") || "";
    const locationIds = useMemo(() => Array.from(new Set(
        locationIdsParam
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
            .concat(legacyLocationId ? [legacyLocationId] : [])
    )), [locationIdsParam, legacyLocationId]);
    const autoPrint = searchParams.get("auto_print") === "1";

    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        const ids = idsStr.split(",").filter(Boolean);
        (async () => {
            if (ids.length === 0) {
                setErr("Geen bestellingen meegegeven.");
                return;
            }
            try {
                const results = [];
                for (const oid of ids) {
                    const q = locationIds.length
                        ? `?location_ids=${encodeURIComponent(locationIds.join(","))}`
                        : "";
                    const d = (await api.get(`/orders/${oid}/summary${q}`)).data;
                    results.push(d);
                }
                if (cancelled) return;
                const staleItems = results.filter((item) => item.totals?.is_stale);
                if (staleItems.length > 0) {
                    setErr(
                        `${staleItems.length} bestelling${staleItems.length === 1 ? " is" : "en zijn"} gewijzigd na de laatste berekening. Bereken eerst opnieuw voordat je samenvattingen print.`
                    );
                    return;
                }
                setItems(results);
            } catch (e) {
                if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail ?? "Laden mislukt"));
            }
        })();
        return () => { cancelled = true; };
    }, [idsStr, locationIds]);

    useEffect(() => {
        if (!autoPrint || !items) return;
        const t = setTimeout(() => window.print(), 600);
        return () => clearTimeout(t);
    }, [autoPrint, items]);

    if (err) {
        return (
            <div className="p-12 max-w-3xl mx-auto" data-testid="print-summaries-error">
                <Link
                    to="/printcentrum"
                    className="text-sm text-ink-500 hover:text-florefy-900 mb-4 inline-flex items-center gap-1"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug naar Printcentrum
                </Link>
                <div className="bg-destructive/10 border border-destructive text-destructive text-sm p-4 rounded-md">
                    {err}
                </div>
            </div>
        );
    }

    if (!items) return <div className="p-12 text-ink-500" data-testid="print-summaries-loading">Samenvattingen laden…</div>;

    return (
        <>
            <PrintSessionBanner />
            <div className="no-print p-8 lg:p-12 max-w-4xl mx-auto" data-testid="print-summaries-preview">
                <PageHeader
                    kicker="Printcentrum"
                    title={`${items.length} samenvatting${items.length === 1 ? "" : "en"}`}
                    subtitle="Elke order krijgt zijn eigen pagina. Print het document via je browser (Cmd/Ctrl+P)."
                    testid="print-summaries-header"
                    actions={
                        <button
                            onClick={() => window.print()}
                            data-testid="btn-print-summaries-now"
                            className="px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2"
                        >
                            <Printer className="h-4 w-4" /> Print nu
                        </button>
                    }
                />
                <div className="bg-white border border-border rounded-md divide-y divide-border">
                    {items.map((d, i) => (
                        <div key={d.order_id || `idx-${i}`} className="px-5 py-3 flex items-center justify-between gap-3 text-sm">
                            <div className="min-w-0">
                                <div className="font-medium text-ink-900 truncate">{d.customer?.name}</div>
                                <div className="text-xs text-ink-500 font-mono">
                                    {d.delivery_date} · {d.totals.total_products} planten · {d.totals.total_units} stuks
                                </div>
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
                                #{i + 1}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Print-only: één pagina per order (per locatie sectie binnen die pagina) */}
            <div className="print-only">
                {items.map((d, idx) => (
                    <SummaryPrintPage key={d.order_id || `idx-${idx}`} data={d} />
                ))}
            </div>
        </>
    );
}

function SummaryPrintPage({ data }) {
    const groups = data.by_location || [];
    return (
        <>
            {groups.map((g) => (
                <div key={g.location_id || "no-loc"} className="print-page p-8 bg-white text-black">
                    <div className="border-b-2 border-black pb-3 mb-5">
                        <div className="font-mono text-[10px] uppercase tracking-widest">
                            Florefy Flow · Samenvatting voor de schuur
                        </div>
                        <div className="text-2xl font-bold mt-1">{data.customer?.name}</div>
                        <div className="text-sm">{data.customer?.address}</div>
                        <div className="text-sm font-mono mt-1">
                            Leverdatum: {data.delivery_date} · {g.total_quantity} stuks
                        </div>
                        {(g.total_carts != null || g.total_plates != null) && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {g.total_carts != null && (
                                    <div className="inline-block px-3 py-1 border-2 border-black font-mono text-sm font-bold">
                                        {g.total_carts} {g.total_carts === 1 ? "kar" : "karren"}
                                    </div>
                                )}
                                {g.total_plates != null && (
                                    <div className="inline-block px-3 py-1 border-2 border-black font-mono text-sm font-bold">
                                        {g.total_plates} {g.total_plates === 1 ? "plaat" : "platen"}
                                    </div>
                                )}
                                {data.totals.is_stale && (
                                    <div className="inline-block px-3 py-1 border-2 border-dashed border-black font-mono text-xs">
                                        ⚠ bestelling bewerkt — opnieuw berekenen
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="mt-2 inline-block px-2 py-1 border-2 border-black font-mono text-sm font-bold">
                            LOCATIE {g.location_code} · {g.location_name}
                        </div>
                    </div>
                    <PrintOptions data={{ ...data, risers: g.risers ?? data.risers }} />
                    <SummaryItems items={g.items} />
                    {data.notes && (
                        <div className="mt-5 text-xs">
                            <span className="font-semibold">Notities:</span> {data.notes}
                        </div>
                    )}
                    <div className="mt-4 text-[10px] font-mono">
                        Florefy Flow — Gegenereerd op {new Date().toLocaleString("nl-NL")}
                    </div>
                </div>
            ))}
        </>
    );
}

function PrintOptions({ data }) {
    return (
        <div className="mb-5 border border-black p-3 text-xs font-mono grid grid-cols-2 gap-x-4 gap-y-1">
            <div><strong>Type kar:</strong> {data.cart_type || "CC"}</div>
            <div><strong>Kwaliteit:</strong> {data.quality || "A"}</div>
            <div><strong>Etiket:</strong> {LABEL_TYPE_LABELS[data.label_type] || "—"}</div>
            <div>
                <strong>Stickers:</strong>{" "}
                {data.stickers ? `Ja${data.stickers_description ? ` (${data.stickers_description})` : ""}` : "Nee"}
            </div>
            <div className="col-span-2">
                <strong>Opzetstukken:</strong> {data.risers ? "Ja — berekend met 42 posities per paal" : "Nee — standaard 35 posities"}
            </div>
        </div>
    );
}

function SummaryItems({ items }) {
    return (
        <div className="divide-y-2 divide-black border-2 border-black">
            {items.map((it) => (
                <div key={`${it.product_id}-${it.location_id}`} className="px-5 py-4">
                    <div className="flex items-baseline justify-between gap-4">
                        <div className="min-w-0">
                            <div className="text-base font-semibold text-ink-900">
                                {it.name}
                                {it.cultivar && (
                                    <span className="italic font-normal text-ink-700 ml-1">{it.cultivar}</span>
                                )}
                                <span className="font-mono text-sm font-normal text-ink-500 ml-2">
                                    {it.pot_size}
                                    {it.pot_liters ? ` · ${it.pot_liters}` : ""}
                                </span>
                            </div>
                            <div className="mt-1 text-sm text-ink-900 font-mono">
                                {formatLayers(it.quantity, it.units_per_layer)}
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-mono font-bold text-xl text-ink-900">{it.quantity}</div>
                            <div className="data-label">stuks</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
